import React from "react";
import { Box, type SxProps, type Theme } from "@mui/material";
import { parseCitations } from "../../../features/narrative-citations/parseCitations";
import { renderCitedSegments } from "../../../features/narrative-citations/renderCitedSegments";
import type { CitationGroup } from "../../../features/narrative-citations/types";

export interface CitedNarrativeProps {
  /** Narrative text containing enriched citation tokens. */
  text: string;
  /** Invoked when a citation anchor is activated (click / Enter / Space). */
  onCite?: (group: CitationGroup) => void;
  sx?: SxProps<Theme>;
}

/**
 * Render narrative text with each cited phrase as a clickable transcript anchor.
 * Citation tokens are decoded and hidden; the phrase they sit behind becomes the
 * link. Falls back to plain text when there are no citations.
 */
export const CitedNarrative: React.FC<CitedNarrativeProps> = ({
  text,
  onCite,
  sx,
}) => {
  const { cleanText, groups } = React.useMemo(
    () => parseCitations(text),
    [text],
  );

  if (groups.length === 0) {
    return (
      <Box sx={{ whiteSpace: "pre-wrap", ...sx }}>
        {cleanText || "No value provided"}
      </Box>
    );
  }

  return (
    <Box sx={{ whiteSpace: "pre-wrap", ...sx }}>
      {renderCitedSegments(cleanText, groups, onCite, "narrative")}
    </Box>
  );
};
